import { useContent } from '@/content'
import type { Field } from './inferFields'
import { ChoiceField } from './ChoiceField'
import { ImageField } from './ImageField'
import { ListEditor } from './ListEditor'
import { TagsInput } from './TagsInput'
import { suggestionsFor, vocabularyFor } from './vocabulary'
import styles from './fields.module.css'

interface RecordFormProps {
  fields: Field[]
  value: Record<string, unknown>
  token: string
  /** Prefix for input ids, so labels stay bound to the right box. */
  path: string
  onChange: (next: Record<string, unknown>) => void
}

/**
 * The list this record sits in — `projects.0.classifications.0` is in
 * `classifications`.
 *
 * Needed because two different fields are called `category`: an id pointing
 * into a domain, and a project's own display label. Nothing but the position
 * tells them apart.
 */
const enclosingKey = (path: string): string | undefined =>
  path
    .split('.')
    .filter((part) => part !== '' && !/^\d+$/.test(part))
    .at(-1)

/** The section, which is the first segment of the path. */
const sectionOf = (path: string): string | undefined => path.split('.')[0]

const asString = (value: unknown): string =>
  typeof value === 'string' ? value : value == null ? '' : String(value)

const asStringArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : []

const asRecord = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}

const isMedia = (field: Field) => field.kind === 'image' || field.kind === 'file'
const isBlock = (field: Field) => field.kind === 'list' || field.kind === 'group'

/**
 * Renders one object as a form, choosing an input per field.
 *
 * Recursive: a `group` field renders another `RecordForm`, a `list` renders
 * a `ListEditor` which renders a `RecordForm` per item. That is what makes a
 * project — with its nested classifications and challenge groups — editable
 * without a bespoke screen for each level of nesting.
 *
 * ## Why the fields are grouped rather than listed in order
 *
 * Source order put a one-line text box next to a picture uploader four times
 * its height. In a two-column grid the row takes the height of the taller
 * cell, so every short field sat in a pocket of empty space and no two
 * labels lined up down the page.
 *
 * Grouping by kind fixes the alignment and the reading order at once: the
 * plain fields together, the pictures together, and anything with a list
 * inside it in a panel of its own. Within each block the cells are the same
 * sort of height, so the columns actually read as columns.
 */
export const RecordForm = ({
  fields,
  value,
  token,
  path,
  onChange,
}: RecordFormProps) => {
  const set = (key: string, next: unknown) => onChange({ ...value, [key]: next })

  /*
   * The live document, which is where every cross-reference resolves: the
   * domains a project can be classified under are the domains section, and
   * the projects a service can point at are the projects section.
   *
   * Live rather than draft, deliberately. A domain has to be saved before it
   * can be assigned, which is the same order the site needs it in — an
   * unsaved domain would be offered here and resolve to nothing on the page.
   */
  const content = useContent() as unknown as Record<string, unknown>
  const context = {
    sectionKey: sectionOf(path),
    parentKey: enclosingKey(path),
    record: value,
    content,
  }

  const plain = fields.filter((f) => !isMedia(f) && !isBlock(f))
  const media = fields.filter(isMedia)
  const blocks = fields.filter(isBlock)

  const control = (field: Field, id: string, current: unknown) => {
    switch (field.kind) {
      case 'longText':
        return (
          <textarea
            id={id}
            value={asString(current)}
            onChange={(event) => set(field.key, event.target.value)}
            className={styles.textarea}
            rows={4}
          />
        )

      case 'choice': {
        const vocabulary = vocabularyFor(field.key, context)
        // No vocabulary resolved — a key that looks like a reference in a
        // section with nothing to point at. A text box beats an empty menu.
        if (!vocabulary) {
          return (
            <input
              id={id}
              type="text"
              value={asString(current)}
              onChange={(event) => set(field.key, event.target.value)}
              className={styles.input}
            />
          )
        }
        return (
          <ChoiceField
            id={id}
            value={asString(current)}
            vocabulary={vocabulary}
            onChange={(next) => set(field.key, next)}
          />
        )
      }

      case 'text':
      case 'url':
        return (
          <input
            id={id}
            type={field.kind === 'url' ? 'url' : 'text'}
            value={asString(current)}
            onChange={(event) => set(field.key, event.target.value)}
            className={styles.input}
            spellCheck={field.kind !== 'url'}
          />
        )

      case 'number':
        return (
          <input
            id={id}
            type="number"
            value={typeof current === 'number' ? current : ''}
            onChange={(event) =>
              // An empty box is not zero. Keep it empty until it parses, or
              // clearing a year would silently write 0.
              set(field.key, event.target.value === '' ? '' : Number(event.target.value))
            }
            className={styles.input}
          />
        )

      case 'boolean':
        return (
          <label className={styles.checkbox}>
            <input
              id={id}
              type="checkbox"
              checked={current === true}
              onChange={(event) => set(field.key, event.target.checked)}
            />
            <span>{current === true ? 'Yes' : 'No'}</span>
          </label>
        )

      case 'image':
      case 'file':
        return (
          <ImageField
            id={id}
            token={token}
            kind={field.kind}
            folder={field.folder ?? 'misc'}
            value={asString(current)}
            onChange={(next) => set(field.key, next)}
          />
        )

      case 'tags':
        return (
          <TagsInput
            id={id}
            value={asStringArray(current)}
            suggestions={suggestionsFor(field.key, content)}
            onChange={(next) => set(field.key, next)}
          />
        )

      case 'group':
        return (
          <RecordForm
            fields={field.fields ?? []}
            value={asRecord(current)}
            token={token}
            path={id}
            onChange={(next) => set(field.key, next)}
          />
        )

      case 'list':
        return (
          <ListEditor
            fields={field.fields ?? []}
            template={field.template}
            token={token}
            path={id}
            items={Array.isArray(current) ? current : []}
            onChange={(next) => set(field.key, next)}
          />
        )

      default:
        return null
    }
  }

  const cell = (field: Field) => {
    const id = `${path}.${field.key}`
    return (
      <div key={field.key} className={styles.field}>
        <label htmlFor={id} className={styles.label}>
          {field.label}
        </label>
        {control(field, id, value[field.key])}
      </div>
    )
  }

  return (
    <div className={styles.record}>
      {plain.length > 0 && <div className={styles.form}>{plain.map(cell)}</div>}

      {media.length > 0 && (
        <section className={styles.block}>
          <h3 className={styles.blockTitle}>Media</h3>
          <div className={styles.form}>{media.map(cell)}</div>
        </section>
      )}

      {blocks.map((field) => {
        const id = `${path}.${field.key}`
        return (
          <section key={field.key} className={styles.block}>
            {/*
              * The label *is* the panel's heading. Kept as a `label` rather
              * than a heading element so it still names the control for a
              * screen reader, which a styled `h3` beside it would not.
              */}
            <label htmlFor={id} className={styles.blockTitle}>
              {field.label}
            </label>
            {control(field, id, value[field.key])}
          </section>
        )
      })}
    </div>
  )
}
